import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { CommandCenterHousehold } from '@ndvr/customer-api';

import { Card } from '@/components/ui/card';
import { useInternalUserDisplayName } from '@/modules/internal-user-queries';
import { getHouseholdName, getPersonName, useHouseholdRoutePaths, useRequiredParams } from 'utils';
import { useCommandCenterHouseholds } from 'views/household/household-queries';
import { Button, CondensedTable, DateTimeDisplay, IconExternalLink, Text, Title, VerticalLayout } from 'widgets';

const columnHelper = createColumnHelper<CommandCenterHousehold>();

export const AffiliationsTab = () => {
    const navigate = useNavigate();
    const { OwnersTabRoutePath } = useHouseholdRoutePaths();
    const { personOrgGUID } = useRequiredParams(['personOrgGUID']);
    const { data: accounts, isLoading } = useCommandCenterHouseholds();
    const getInternalUserName = useInternalUserDisplayName();

    const ffAffiliations = useMemo(() => {
        return accounts?.filter((account) => account.ffEmployeePersonOrgGUID === personOrgGUID) ?? [];
    }, [accounts, personOrgGUID]);

    const columns = [
        columnHelper.accessor((data) => getHouseholdName(data), {
            id: 'name',
            header: 'Account Name',
            cell: ({ getValue }) => (
                <Text largeBody $truncated $themeColor="link">
                    {getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor(
            (data) =>
                getPersonName({
                    firstName: data.primaryManagerFirstNamePlain,
                    lastName: data.primaryManagerLastNamePlain,
                }),
            {
                id: 'principal',
                header: 'Principal',
                cell: ({ getValue }) => (
                    <Text largeBody $truncated $themeColor="link">
                        {getValue()}
                    </Text>
                ),
            },
        ),
        columnHelper.accessor((data) => getInternalUserName(data.primaryAdvisorGUID), {
            id: 'primaryAdvisor',
            header: 'Primary Advisor',
        }),
        columnHelper.accessor('latestLoginOnAccount', {
            id: 'latestLogin',
            header: 'Latest Login',
            cell: ({ getValue }) => <DateTimeDisplay dateString={getValue()} />,
            size: 200,
        }),
        columnHelper.accessor('updatedAt', {
            id: 'updatedAt',
            header: 'Updated',
            cell: ({ getValue }) => <DateTimeDisplay dateString={getValue()} />,
            size: 200,
        }),
        columnHelper.display({
            id: 'actions',
            cell: ({ row }) => (
                <Button
                    size="icon"
                    buttonType="ghost"
                    onClick={() => navigate(OwnersTabRoutePath(row.original.globalAccountGUID))}
                    icon={<IconExternalLink size="xSmall" />}
                />
            ),
            size: 60,
        }),
    ];

    return (
        <VerticalLayout $gap={32}>
            <Title section>Affiliations</Title>

            <VerticalLayout $gap={8}>
                <Title content>Friends and Family Program</Title>
                <Card>
                    <CondensedTable columns={columns} data={ffAffiliations} isLoading={isLoading} />
                </Card>
            </VerticalLayout>
        </VerticalLayout>
    );
};
